import { useEffect, useMemo, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useSharedState } from "state";
import PageLayout from "components/layout/page-layout";
import moment from "moment";
import Table, { tableHeader } from "components/table";
import { oneLine, tagsList } from "components/table/table-parts";
import ActionButton from "components/table/action-btn";
import { IconFolderOpen, IconPencil, IconTrash } from "@tabler/icons-react";
import { Formula } from "types/formula";
import DangerModal from "components/modal/danger";
import { deleteFormula } from "api/budget_request";

export default function Formulas() {
  const { formulas, getFormulasData, notify } = useSharedState();

  const navigate = useNavigate();

  const [formulaToDelete, setFormulaToDelete] = useState<Formula | undefined>(undefined);
  const [isFormLoading, setFormLoading] = useState<boolean>(false);

  const deleteFormulaDanger = useMemo(() => {
    return `Вы действительно хотите удалить формулу "${formulaToDelete?.formula_name}"?`;
  }, [formulaToDelete]);

  const [lastFetchedPath, setLastFetchedPath] = useState<string>("");

  useEffect(() => {
    if (window.location.pathname === "/formulas" && lastFetchedPath !== window.location.pathname) {
      getFormulasData();
      setLastFetchedPath(window.location.pathname);
    }
  }, [window.location.pathname]);

  const handleFormulaDelete = async () => {
    try {
      setFormLoading(true);
      await deleteFormula(formulaToDelete?.budget_formula_id!);
      notify("Формула удалена", "success");

      await getFormulasData();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при удалении формулы");
    } finally {
      setFormLoading(false);
    }
  };

  const formulasTableHeaders: tableHeader[] = [
    { header_text: "№", narrow: true, wrap: false },
    { header_text: "Название", narrow: false, wrap: false },
    { header_text: "Формула", narrow: false, wrap: false },
    { header_text: "Действия", narrow: false, wrap: false },
  ];

  const formulasList = (
    <>
      {formulas.map((formula, index) => (
        <tr key={index}>
          <td>{oneLine((index + 1).toString())}</td>
          <td>{oneLine(formula.formula_name || "-")}</td>
          <td>{tagsList(formula.expression_list || [])}</td>
          <td>
            <div className="btn-list flex-nowrap">
              <ActionButton
                icon={<IconPencil className="icon" />}
                title="Изменить"
                action={() => navigate(`${formula.budget_formula_id}`)}
                disabled={isFormLoading}
                color={"info"}
                modal={false}
              />
              <ActionButton
                icon={<IconTrash className="icon" />}
                title="Удалить"
                action={() => setFormulaToDelete(formula)}
                disabled={isFormLoading}
                color={"danger"}
                modal={true}
                modal_id="#modal-delete-formula"
              />
            </div>
          </td>
        </tr>
      ))}
    </>
  );

  const formulasTableComponent = Table(formulasTableHeaders, formulasList, false);

  return (
    <>
      <PageLayout
        children={formulasTableComponent}
        header={"Формулы"}
        subheaderText={`Количество формул: ${formulas.length}`}
        search={false}
        filters={false}
        buttonText={"Новая формула"}
        buttonAction={() => navigate("create")}
      />
      <DangerModal
        modal_id="modal-delete-formula"
        dangerDescription={deleteFormulaDanger}
        cancel={() => setFormulaToDelete(undefined)}
        confirm={handleFormulaDelete}
      />
      <Outlet />
    </>
  );
}
